import { all, call, takeLatest, put, select } from 'redux-saga/effects';

import * as loadingStates from '@coconut/constants/loadingState';

import {
  types,
  sendMessageSuccess,
  sendMessageFailed,
  deleteMessageSuccess,
  deleteMessageFailed,
  resendMessageSuccess,
  resendMessageFailed,
} from '../redux/sendChat';
import {
  appendMessage,
  updateMessage,
  deleteCurrentMessage,
  getCurrentMessages,
} from '../redux/chatsMessage';
import { appendImages } from '../redux/images';
import * as mimeTypes from '@coconut/constants/mimeTypes';
import { CONTENT_ACTION } from '../constants/messageActions';
import apis from '../apis/chat';
import { getCapitalizedRegionLang } from '@coconut/utils/langParser';

function getFormattedMessage(message) {
  switch (message.mtype) {
    case mimeTypes.TEXT:
      return {
        description: message.description,
        lang: message.lang,
        mtype: message.mtype,
      };
    case mimeTypes.AUDIO: {
      const formData = new FormData();
      formData.append('audio_record', message.audioMp3);
      formData.append('lang', message.lang);
      formData.append('mtype', message.mtype);

      return formData;
    }
    case mimeTypes.IMAGE: {
      const formData = new FormData();
      formData.append('image', message.image, message.image.name);
      formData.append('lang', message.lang);
      formData.append('mtype', message.mtype);

      return formData;
    }
    default:
      return {};
  }
}

function* deleteMessageFlow(action) {
  const { id } = action.payload;

  try {
    yield put(deleteCurrentMessage(id));
    yield put(deleteMessageSuccess());
  } catch (err) {
    deleteMessageFailed(err);
  }
}

function* resendMessageFlow(action) {
  const { id, QID } = action.payload;

  const currentMessages = yield select(getCurrentMessages);
  const messageToSend = currentMessages.find((message) => message.id === id);
  const messageData = getFormattedMessage(messageToSend);

  try {
    // renew the data of newly appended message.
    yield put(
      updateMessage({
        messageStatus: loadingStates.LOADING,
        newMessageID: id,
        message: messageToSend,
      })
    );
    const response = yield call(apis.sendChatMessage, QID, messageData);

    const {
      data: { message },
    } = response;

    // renew the data of newly appended message.
    yield put(
      updateMessage({
        messageStatus: loadingStates.READY,
        newMessageID: id,
        message,
      })
    );

    if (message.mtype === mimeTypes.IMAGE) {
      // If image message, append to image reducer
      yield put(appendImages([message]));
    }

    yield put(resendMessageSuccess(message));
  } catch (err) {
    yield put(
      updateMessage({
        messageStatus: loadingStates.ERROR,
        message: messageToSend,
        newMessageID: id,
      })
    );

    yield put(resendMessageFailed(err));
  }
}

function* sendTextMessageFlow(action) {
  const { description, lang, mtype, QID, userID } = action.payload;

  const newMessageID = Date.now();
  const newMessage = {
    snapask_uid: userID,
    loadingStatus: loadingStates.LOADING,
    message_action: CONTENT_ACTION,
    lang: getCapitalizedRegionLang(lang),
    mtype,
    description,
    created_at: Date.now(),
  };

  try {
    // append message to reducer
    yield put(
      appendMessage({
        id: newMessageID,
        ...newMessage,
      })
    );

    const response = yield call(apis.sendChatMessage, QID, {
      description,
      lang: getCapitalizedRegionLang(lang),
      mtype,
    });

    const {
      data: { message },
    } = response;

    // renew the data of newly appended message.
    yield put(
      updateMessage({
        messageStatus: loadingStates.READY,
        newMessageID,
        message,
      })
    );

    yield put(sendMessageSuccess(message));
  } catch (err) {
    yield put(
      updateMessage({
        messageStatus: loadingStates.ERROR,
        message: {
          ...newMessage,
          id: newMessageID,
        },
        newMessageID,
      })
    );
    yield put(sendMessageFailed(err));
  }
}

function* sendAudioMessageFlow(action) {
  const { audioRecord, QID, userID, lang, mtype } = action.payload;

  // retrieve audio data
  const [audioMp3] = audioRecord;

  const formData = new FormData();
  formData.append('audio_record', audioMp3);
  formData.append('lang', getCapitalizedRegionLang(lang));
  formData.append('mtype', mtype);

  const newMessageID = Date.now();
  const newMessage = {
    audio_record_url: URL.createObjectURL(audioMp3),
    snapask_uid: userID,
    loadingStatus: loadingStates.LOADING,
    message_action: CONTENT_ACTION,
    lang,
    mtype,
    created_at: Date.now(),
  };

  try {
    // append message to current stack first.
    yield put(
      appendMessage({
        id: newMessageID,
        ...newMessage,
      })
    );

    // append audio record
    const response = yield call(apis.sendChatMessage, QID, formData);

    const {
      data: { message },
    } = response;

    yield put(
      updateMessage({
        messageStatus: loadingStates.READY,
        newMessageID,
        message,
      })
    );

    yield put(sendMessageSuccess(message));
  } catch (err) {
    yield put(
      updateMessage({
        messageStatus: loadingStates.ERROR,
        message: {
          ...newMessage,
          id: newMessageID,
        },
        newMessageID,
      })
    );

    yield put(sendMessageFailed(err));
  }
}

function* sendImageMessageFlow(action) {
  const { image, lang, mtype, QID, userID } = action.payload;

  const newMessageID = Date.now();
  const formData = new FormData();
  formData.append('image', image, image.name);
  formData.append('lang', getCapitalizedRegionLang(lang));
  formData.append('mtype', mtype);

  const newMessage = {
    snapask_uid: userID,
    loadingStatus: loadingStates.LOADING,
    message_action: CONTENT_ACTION,
    lang,
    image,
    mtype,
    created_at: Date.now(),
  };

  try {
    // append message to reducer
    yield put(
      appendMessage({
        id: newMessageID,
        ...newMessage,
      })
    );

    const response = yield call(apis.sendChatMessage, QID, formData);

    const {
      data: { message },
    } = response;

    // renew the data of newly appended message.
    yield put(
      updateMessage({
        messageStatus: loadingStates.READY,
        newMessageID,
        message,
      })
    );

    yield put(sendMessageSuccess(message));
    // append image to image reducer.
    yield put(appendImages([message]));
  } catch (err) {
    yield put(
      updateMessage({
        message: {
          ...newMessage,
          id: newMessageID,
        },
        messageStatus: loadingStates.ERROR,
        newMessageID,
      })
    );
    yield put(sendMessageFailed(err));
  }
}

export default function* () {
  yield all([
    takeLatest(types.SEND_TEXT_MESSAGE, sendTextMessageFlow),
    takeLatest(types.SEND_AUDIO_MESSAGE, sendAudioMessageFlow),
    takeLatest(types.SEND_IMAGE_MESSAGE, sendImageMessageFlow),
    takeLatest(types.RESEND_MESSAGE, resendMessageFlow),
    takeLatest(types.DELETE_MESSAGE, deleteMessageFlow),
  ]);
}



// WEBPACK FOOTER //
// ./src/scenes/Chat/services/sagas/sendChat.js